import { useEffect, useMemo, useState } from 'react';

import {
  faInfoCircle,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

import routes from '@/routes/routes.constants';
import { useDevtoolsShortcut } from '@/utils/hooks/useDevtoolsShortcut';

import type {
  InsuranceStatusCtaValue,
  StatusTypeValue,
  ZuubPatientData,
  ZuubPatientDetailedData,
} from '../../../@types/zuub/contracts';
import type { TableColumn } from '../../../common-components/composed/sortableTable/SortableTable';
import { SortableTable } from '../../../common-components/composed/sortableTable/SortableTable';
import { StatusChip } from '../../../common-components/elements/StatusChip';
import type { Appointment } from '../../../react-query/useAppointments';
import { toBusinessName } from '../../../utils/string';
import { useLocalizedFormatDate } from '../../claims/freeClaims/state';
import { PatientInfo } from '../../patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import { selectedPayerIdAtom } from '../atoms';
import { getVerificationStatusDisplay } from '../helpers';

import { ActionsColumn } from './ActionsColumn';
import { EnableAdditionalBenefitsInfoHoverCard } from './EnableAdditionalBenefitsInfoHoverCard';

// Row type for the table - each detailed data item becomes a row
export type AutoVerifyTableRow = {
  detailedData: ZuubPatientDetailedData;
  appointment: Appointment | null;
  zuubPatient: ZuubPatientData;
};

// Map CTA code to display string
const getInsuranceStatusCtaDisplay = (cta: InsuranceStatusCtaValue): string => {
  switch (cta) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'CHECK_MANUALLY':
      return 'Check manually';
    case 'CHECK_VIDEA_LOGIN':
      return 'Check Videa log-in';
    case 'CONFIRM_PATIENT_DETAILS':
      return 'Confirm patient details';
    case 'NOT_ELIGIBLE':
      return 'Not Eligible';
    case 'CONFIRM_NPI_NUMBER':
      return 'Confirm NPI number';
    case 'NO_PAYER_LISTED':
      return 'No Payer Listed';
    default:
      return cta;
  }
};

// Map statusType to StatusChip variant
const getStatusTypeVariant = (
  statusType: StatusTypeValue,
): 'success' | 'warning' | 'error' | 'default' => {
  switch (statusType) {
    case 'success':
      return 'success';
    case 'warn':
      return 'warning';
    case 'error':
      return 'error';
    case 'action':
      return 'default'; // Light grey background
    default:
      return 'default';
  }
};

type SortField = 'flag' | 'patient' | 'appointment' | 'procedureCodes';
type SortDirection = 'asc' | 'desc';

interface AutoVerifyTableProps {
  rows: AutoVerifyTableRow[];
  isSearchActive?: boolean;
}

export const AutoVerifyTable = ({
  rows,
  isSearchActive = false,
}: AutoVerifyTableProps) => {
  // state
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [devInfoModalData, setDevInfoModalData] = useState<Record<
    string,
    unknown
  > | null>(null);

  // hooks
  const { showDevtools } = useDevtoolsShortcut();

  // atoms
  const setSelectedPayerIdAtom = useSetAtom(selectedPayerIdAtom);

  // hooks
  const formatLocalizedDate = useLocalizedFormatDate();
  const history = useHistory();

  // Sort rows based on current sort settings
  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'appointment': {
          const dateA = a.appointment?.appointment.start || '';
          const dateB = b.appointment?.appointment.start || '';
          comparison = dateA.localeCompare(dateB);
          break;
        }
        case 'patient': {
          const nameA = a.detailedData.patient.displayName.toLowerCase();
          const nameB = b.detailedData.patient.displayName.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [rows, sortField, sortDirection]);

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  // Define columns for SortableTable
  const columns: TableColumn<AutoVerifyTableRow>[] = [
    {
      key: 'flag',
      header: '',
      width: 'vai-w-[50px]',
      isSortable: true,
      render: (row) => {
        const shouldShowFlag = row.detailedData.needsPayerPortalConnection;

        return (
          <EnableAdditionalBenefitsInfoHoverCard
            isOpen={shouldShowFlag}
            onSetUpCredentials={() => {
              const payerId = row.detailedData.payerId;
              if (payerId) {
                setSelectedPayerIdAtom(payerId);
                history.push(routes.CONNECT_PAYERS);
              }
            }}
          />
        );
      },
    },
    {
      key: 'appointment',
      header: <Trans>Appointment</Trans>,
      width: 'vai-w-[150px]',
      isSortable: true,
      render: (row) => {
        if (!row.appointment) {
          return <>—</>;
        }
        return (
          <>
            {isSearchActive
              ? formatLocalizedDate(
                  row.appointment.appointment.start,
                  false,
                  'P',
                )
              : formatLocalizedDate(
                  row.appointment.appointment.start,
                  false,
                  'h:mmaaa',
                )}
          </>
        );
      },
    },
    {
      key: 'patient',
      header: <Trans>Patient</Trans>,
      width: 'vai-w-[200px]',
      isSortable: true,
      render: (row, index) => {
        if (!row.appointment) {
          // Fallback to zuub patient data if no appointment
          return (
            <div className="vai-font-semibold">
              {row.detailedData.patient.displayName}
            </div>
          );
        }
        return (
          <div className="vai-font-semibold">
            <PatientInfo
              context={PatientListPageLayoutContextMap.AutoVerify}
              appointment={row.appointment}
              index={index}
            />
          </div>
        );
      },
    },
    {
      key: 'payerName',
      header: <Trans>Payer Name</Trans>,
      width: 'vai-w-[200px]',
      render: (row) => {
        const payerName = row.detailedData.payerName;
        return (
          <div className="vai-flex vai-items-center vai-h-[40px]">
            {payerName ? toBusinessName(payerName) : '—'}
          </div>
        );
      },
    },
    {
      key: 'verificationStatus',
      header: <Trans>Verification Status</Trans>,
      width: 'vai-w-[150px]',
      render: (row) => {
        const data = row.detailedData;
        const verificationStatus = data.verificationStatus
          ? getVerificationStatusDisplay(data.verificationStatus)
          : {
              status: 'Unknown',
              icon: faQuestionCircle,
              iconColor: 'vai-text-gray-500',
            };

        return (
          <div className="vai-flex vai-items-center vai-gap-2 vai-h-[40px]">
            <StatusChip
              variant="ghost"
              icon={<FontAwesomeIcon icon={verificationStatus.icon} />}
              text={verificationStatus.status}
              iconColor={verificationStatus.iconColor}
            />
            {showDevtools && (
              <>
                {data.resultSource && (
                  <span className="vai-text-xs vai-px-2 vai-py-0.5 vai-bg-gray-200 vai-text-gray-700 vai-rounded-full">
                    {data.resultSource}
                  </span>
                )}
                {data.source && (
                  <span className="vai-text-xs vai-px-2 vai-py-0.5 vai-bg-blue-100 vai-text-blue-700 vai-rounded-full">
                    {data.source}
                  </span>
                )}
                {data.mismatches && (
                  <button
                    onClick={() => setDevInfoModalData(data.mismatches!)}
                    className="vai-text-gray-500 hover:vai-text-gray-700"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="vai-w-4 vai-h-4"
                    />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      key: 'insuranceStatus',
      header: <Trans>Insurance Status</Trans>,
      width: 'vai-w-[180px]',
      render: (row) => {
        const data = row.detailedData;
        const insuranceStatusV2 = data.insuranceStatusV2;
        const statusType = data.statusType;

        if (!insuranceStatusV2) {
          return (
            <div className="vai-flex vai-items-center vai-h-[40px]">
              <StatusChip variant="ghost" text="Unknown" />
            </div>
          );
        }

        const variant = statusType
          ? getStatusTypeVariant(statusType)
          : 'default';

        return (
          <div className="vai-flex vai-items-center vai-h-[40px]">
            <StatusChip
              variant={variant}
              text={getInsuranceStatusCtaDisplay(insuranceStatusV2)}
            />
          </div>
        );
      },
    },
    {
      key: 'lastRun',
      header: <Trans>Last Run</Trans>,
      width: 'vai-w-[120px]',
      render: (row) => {
        const lastRun = row.zuubPatient.lastRun;
        return <>{lastRun ? formatLocalizedDate(lastRun) : '—'}</>;
      },
    },
    {
      key: 'actions',
      header: <Trans>Actions</Trans>,
      width: 'vai-w-[150px]',
      render: (row) => {
        return (
          <ActionsColumn
            appointment={row.appointment}
            zuubPatient={row.zuubPatient}
            onManagePayerConnection={(payerId) => {
              setSelectedPayerIdAtom(payerId);
              history.push(routes.CONNECT_PAYERS);
            }}
          />
        );
      },
    },
  ];

  // When searching, default to descending order for appointment dates
  useEffect(() => {
    if (
      isSearchActive &&
      sortField === 'appointment' &&
      sortDirection === 'asc'
    ) {
      setSortDirection('desc');
    }
  }, [isSearchActive, sortField, sortDirection]);

  return (
    <>
      <SortableTable
        data={sortedRows}
        columns={columns}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        rowKey={(row) =>
          `auto-verify-${row.detailedData.unifiedAppointmentId}-${row.detailedData.pmsPatientId}`
        }
        className="vai-h-full"
      />

      {/* Dev Info Modal */}
      <Dialog
        open={devInfoModalData !== null}
        onOpenChange={(isOpen) => !isOpen && setDevInfoModalData(null)}
      >
        <DialogContent className="vai-max-w-2xl vai-max-h-[80vh] vai-overflow-auto">
          <DialogHeader>
            <DialogTitle>
              <Trans>Dev Info - Mismatches</Trans>
            </DialogTitle>
          </DialogHeader>
          <pre className="vai-bg-gray-100 vai-p-4 vai-rounded vai-text-xs vai-overflow-auto vai-max-h-[60vh]">
            {JSON.stringify(devInfoModalData, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
};
